/**
 * Maps a GetPaymentDetail `EndToEndDetail` to the `PaymentDetail` view model.
 *
 * Explicit, field-by-field translation of exactly what the page renders: the
 * summary identifiers, invoice ids and reissue block, the response-detail
 * table, and the execution / artifact reference tables. The flow graphs are
 * mapped by flowGraphMapper.
 */
import { EndToEndDetail, ApiResponseDetail, ApiPaymentDetails } from './paymentDetailApi';
import {
  PaymentDetail, KeyValue, ResponseDetailRow,
  ExecutionRefRow, ExecutionRefGroup, ArtifactRefRow, ArtifactRefGroup,
} from './paymentDetailTypes';
import {
  bottleneckLabel, formatMs, fullTimestamp, titleCase, toDirection, toFlowState,
} from './paymentDetailFormat';
import { toFlow } from './flowGraphMapper';

/** Some plugins serialise missing strings as the literal "null". */
const present = (value?: string | null): string | undefined =>
  value && value !== 'null' ? value : undefined;

/** Display value: the string, or "-" when empty / "null". */
const dash = (value?: string | null): string => present(value) ?? '-';

/** Scalar identifier fields, mapped explicitly from the API in display order; "-" when empty. */
const toIdentifiers = (detail: EndToEndDetail): KeyValue[] => {
  const pd = detail.paymentDetails ?? {};
  return [
    { key: 'paymentClientId', value: present(pd.paymentSourceId) ?? '-' },
    { key: 'amazonInternalSignature', value: present(detail.amazonInternalSignature) ?? '-' },
    { key: 'paymentMethod', value: present(pd.paymentMethod) ?? '-' },
    { key: 'paymentType', value: present(pd.paymentType) ?? '-' },
    { key: 'pluginName', value: present(pd.pluginName) ?? '-' },
    { key: 'disbursementId', value: present(detail.disbursementId) ?? '-' },
  ];
};

/** Invoice numbers from paymentDetails.invoices (the invoice id shown in the UI). */
const toInvoiceIds = (pd: ApiPaymentDetails): string[] =>
  (pd.invoices ?? []).map(invoice => present(invoice.invoiceNumber)).filter((id): id is string => !!id);

/** Reissue details, as its own labelled block — only when the payment is a reissue. */
const toReissue = (detail: EndToEndDetail): KeyValue[] | undefined => {
  const r = detail.reissueDetails;
  if (!r) return undefined;
  return [
    { key: 'reissueOf', value: present(r.reissueOf) ?? '-' },
    { key: 'reissueTimestamp', value: present(r.reissueTimestamp) ?? '-' },
    { key: 'reissuePaymentMethod', value: present(r.reissuePaymentMethod) ?? '-' },
  ];
};

/** Epoch ms for sorting; undated responses sort last. */
const receivedMs = (rd: ApiResponseDetail): number => {
  const t = rd.receivedTimeStamp ? Date.parse(rd.receivedTimeStamp) : NaN;
  return Number.isNaN(t) ? Infinity : t;
};

/** Decoded bank/processor responses (responseDetailsById) → table rows, sorted by received time. */
const toResponseDetails = (detail: EndToEndDetail): ResponseDetailRow[] =>
  Object.entries(detail.responseDetailsById ?? {})
    .sort(([, a], [, b]) => receivedMs(a) - receivedMs(b))
    .map(([key, rd]) => ({
      id: key,
      responseIdentifier: dash(rd.responseIdentifier),
      type: dash(rd.type),
      systemId: dash(rd.systemId),
      responseStatus: dash(rd.responseStatus),
      responseCode: dash(rd.responseCode),
      responseCodeDetail: dash(rd.responseCodeDetail),
      responseDescription: dash(rd.responseDescription),
      receivedTimeStamp: rd.receivedTimeStamp ? fullTimestamp(rd.receivedTimeStamp) : '-',
    }));

/** Direction display order: Outbound first, Inbound next, anything else last. */
const DIRECTION_ORDER = ['OUTBOUND', 'INBOUND'];
const directionRank = (direction: string): number => {
  const index = DIRECTION_ORDER.indexOf(direction);
  return index === -1 ? DIRECTION_ORDER.length : index;
};

/** Execution references grouped by direction (Outbound first). */
const toExecutionGroups = (detail: EndToEndDetail): ExecutionRefGroup[] => {
  const groups = new Map<string, ExecutionRefRow[]>();
  (detail.executionRefs ?? []).forEach((ref, index) => {
    const direction = present(ref.direction)?.toUpperCase() ?? 'UNKNOWN';
    const rows = groups.get(direction) ?? [];
    rows.push({
      id: `${ref.executionId}-${index}`,
      executionId: dash(ref.executionId),
      executionType: dash(ref.executionType),
      system: dash(ref.system),
    });
    groups.set(direction, rows);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => directionRank(a) - directionRank(b))
    .map(([direction, rows]) => ({ direction, rows }));
};

/** Artifact references grouped by direction (Outbound first). */
const toArtifactGroups = (detail: EndToEndDetail): ArtifactRefGroup[] => {
  const groups = new Map<string, ArtifactRefRow[]>();
  (detail.artifactRefs ?? []).forEach((ref, index) => {
    const direction = present(ref.direction)?.toUpperCase() ?? 'UNKNOWN';
    const rows = groups.get(direction) ?? [];
    rows.push({
      id: `${ref.artifactId}-${index}`,
      artifactId: dash(ref.artifactId),
      artifactType: dash(ref.artifactType),
    });
    groups.set(direction, rows);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => directionRank(a) - directionRank(b))
    .map(([direction, rows]) => ({ direction, rows }));
};

/* ------------------------------------------------------------------ */
/* Entry point                                                         */
/* ------------------------------------------------------------------ */

export const mapPaymentDetail = (detail: EndToEndDetail): PaymentDetail => {
  const pd = detail.paymentDetails ?? {};
  const overallState = toFlowState(detail.beaconPaymentState);
  const currentDirection = toDirection(detail.currentDirection);

  return {
    id: detail.endToEndId,
    statusLabel: `${detail.beaconPaymentStatus} · ${detail.beaconPaymentState}`,
    statusType: overallState === 'SUCCESS' ? 'success' : overallState === 'FAILED' ? 'error' : 'in-progress',
    locationBadge: `Currently at ${detail.currentSystem} · ${titleCase(currentDirection)}`,
    paymentStatus: present(pd.paymentStatus),
    identifiers: toIdentifiers(detail),
    flow: toFlow(detail.graphs ?? [], detail.totalLatencyMs),
    bottleneckLabel: bottleneckLabel(detail.bottleneck),
    totalLatency: formatMs(detail.totalLatencyMs),
    invoiceIds: toInvoiceIds(pd),
    reissue: toReissue(detail),
    responseDetails: toResponseDetails(detail),
    executions: toExecutionGroups(detail),
    artifacts: toArtifactGroups(detail),
    correlationTags: detail.correlationTags ?? [],
  };
};
